/* eslint-disable max-len */
import React, { FC, useState, useEffect, SyntheticEvent, MouseEvent, KeyboardEvent } from 'react';

import * as Styled from './styles';
import handleKeyPress from '../../../../../common/handleKeyPress';
import BananatagButton from '../../../../../componentLibrary/BananatagButton';
import EmptyState from './EmptyState';
import { IImageColumnProps } from '../../../../../types/imageLibrary';
import * as contentLibraryAction from '../../../../../actions/contentLibrary';
import rules from '../../../../../constants/businessRules/businessRules';

const ImageColumn: FC<IImageColumnProps> = ({
    contents,
    selectedImages,
    onSelect,
    displayPreview,
    canManageLibrary,
    isLoading,
    contentOffset,
    setContentOffset,
    selectedFolderID,
    dispatch,
}) => {
    const [imageHovered, setImageHovered] = useState<number | null>();
    const selectedIDs = selectedImages.map((selection) => selection.contentID);

    // we assume the initial content is at least batch size if we consider fetching more items
    const [contentLengthBeforeLazyLoad, setContentLengthBeforeLazyLoad] = useState(rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE);
    useEffect(() => {
        setContentLengthBeforeLazyLoad(rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE);
        setContentOffset(rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE);
    }, [selectedFolderID, setContentOffset]);

    if (contents.length === 0 && !isLoading) {
        return <EmptyState canManageLibrary={canManageLibrary} />;
    }

    return (
        <Styled.ImageColumn
            displayPreview={displayPreview}
            onScroll={(e: SyntheticEvent) => {
                const { clientHeight, scrollHeight, scrollTop } = e.currentTarget;
                if (Math.round(clientHeight + scrollTop) === scrollHeight) {
                    // it will set to fetch more content if we were able to load all batch items
                    if (
                        contentLengthBeforeLazyLoad === rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE ||
                        Math.abs(contentLengthBeforeLazyLoad - contents.length) === rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE
                    ) {
                        dispatch(
                            contentLibraryAction.updateContent(rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE, contentOffset)
                        );
                        setContentOffset(contentOffset + rules.IMAGE_LIBRARY_LOAD_BATCH_SIZE);
                        setContentLengthBeforeLazyLoad(contents.length);
                    }
                }
            }}
        >
            {contents.map((content) => {
                const isSelected = selectedIDs.includes(content.contentID);
                // TODO: refactor hover logic to not set state as it causes unnecessary rerenders
                const isHovering = imageHovered === content.contentID;
                return (
                    <div key={content.contentID} id={`${content.contentID}`}>
                        <Styled.ImageDiv
                            onClick={(e: MouseEvent<SVGElement>) => onSelect({ e, content })}
                            onKeyDown={(e: KeyboardEvent<SVGElement>) =>
                                handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, () =>
                                    onSelect({ e, content })
                                )
                            }
                            onMouseEnter={() => setImageHovered(content.contentID)}
                            onMouseLeave={() => setImageHovered(null)}
                            onFocus={() => setImageHovered(content.contentID)}
                            onBlur={() => setImageHovered(null)}
                            isSelected={isSelected}
                            data-test-id={`image-thumbnail-${content.contentID}`}
                        >
                            <Styled.ImageThumbnail
                                isHovering={isHovering}
                                src={content.thumbnailURL}
                                alt="This is a preview of your content"
                            />
                            <Styled.ImageActions>
                                {canManageLibrary && (
                                    <Styled.CheckBoxBackground isHovering={isHovering} isSelected={isSelected}>
                                        <Styled.CheckBox
                                            icon={isSelected ? ['fas', 'check-square'] : ['far', 'square']}
                                            onClick={(e: MouseEvent<SVGElement>) =>
                                                onSelect({ e, content, isCheckbox: true })
                                            }
                                            onKeyDown={(e: KeyboardEvent<SVGElement>) =>
                                                handleKeyPress(e as unknown as KeyboardEvent<HTMLElement>, () =>
                                                    onSelect({ e, content, isCheckbox: true })
                                                )
                                            }
                                        />
                                    </Styled.CheckBoxBackground>
                                )}
                                <BananatagButton
                                    size="small"
                                    text="preview"
                                    ariaLabel="Preview image"
                                    type="outline"
                                    styling={{
                                        backgroundColor: 'white',
                                        opacity: isHovering ? 1 : 0,
                                        position: 'absolute',
                                        top: 40,
                                    }}
                                />
                            </Styled.ImageActions>
                        </Styled.ImageDiv>
                    </div>
                );
            })}
        </Styled.ImageColumn>
    );
};

export default ImageColumn;
